// Building generators: machine bodies by type × level (press / grinder / smelter with their moving part
// drawn at rest, M8 merge), deposits by ore × level, the drill head overlay, chute bodies by entry × exit,
// the chute chevron overlay and the moving parts (UX §6.4: "moving parts are separate sprites" — bit,
// piston, wheel, mouth glow — so Unity / M10 can tween them later).
import type { Ctx2D } from '../ctx.ts';
import { drawParts, fillP, outlineUnits, type Part } from '../passes.ts';
import { circle, poly, regularPts, rrect, smoothPoly } from '../shapes.ts';
import type {
  BuildingSpec,
  ChevronSpec,
  ChuteSpec,
  DepositSpec,
  Dir,
  DrillHeadSpec,
  Generator,
  MovingPartSpec,
} from '../types.ts';
import { bodyParts, drawMachine, type MachKit, machKit, withTopTrim } from './body.ts';
import { drawChevrons, drawChuteBody, drawCornerChevrons } from './chute.ts';
import {
  casterParts,
  grinderParts,
  loaderParts,
  packerParts,
  pressParts,
  smelterParts,
} from './processors.ts';
import {
  crateParts,
  depositParts,
  dockParts,
  drillBaseParts,
  drillHeadParts,
  galleryParts,
  gunsmithParts,
} from './structures.ts';

/** Function trim token by building type (UX §6.1 "Machine function colours"). */
export function fnToken(type: string): string {
  return `machine.${type === 'deposit' ? 'crate' : type}`;
}

const DECOR: Record<string, (m: MachKit) => Part[]> = {
  caster: casterParts,
  grinder: grinderParts,
  smelter: smelterParts,
  press: pressParts,
  loader: loaderParts,
  packer: packerParts,
  drill: drillBaseParts,
  gunsmith: gunsmithParts,
  dock: dockParts,
};

/**
 * Moving parts (UX §6.4): frame size and pivot (cells) and where the pivot sits in the building cell —
 * drawing geometry, like the part outlines. `rest` = drawn into the body frame at rest (the board atlas has
 * one body frame per type × level, M5 decision 5); the drill bit belongs to the rotated head (M10 tweens).
 */
export const MOVING_PARTS: readonly {
  type: BuildingSpec['type'];
  part: MovingPartSpec['part'];
  index: number;
  w: number;
  h: number;
  px: number;
  py: number;
  ax: number;
  ay: number;
  rest: boolean;
}[] = [
  {
    type: 'drill',
    part: 'bit',
    index: 0,
    w: 0.3,
    h: 0.34,
    px: 0.5,
    py: 1,
    ax: 0.5,
    ay: 0.16,
    rest: false,
  },
  {
    type: 'drill',
    part: 'bit',
    index: 1,
    w: 0.3,
    h: 0.34,
    px: 0.5,
    py: 1,
    ax: 0.5,
    ay: 0.16,
    rest: false,
  },
  {
    type: 'press',
    part: 'piston',
    index: 0,
    w: 0.44,
    h: 0.3,
    px: 0.5,
    py: 0,
    ax: 0.5,
    ay: 0.2,
    rest: true,
  },
  {
    type: 'grinder',
    part: 'wheel',
    index: 0,
    w: 0.34,
    h: 0.34,
    px: 0.5,
    py: 0.5,
    ax: 0.76,
    ay: 0.42,
    rest: true,
  },
  {
    type: 'smelter',
    part: 'glow',
    index: 0,
    w: 0.36,
    h: 0.36,
    px: 0.5,
    py: 0.5,
    ax: 0.5,
    ay: 0.6,
    rest: true,
  },
];

export const buildingGen: Generator<BuildingSpec> = (c, spec, px, env) => {
  c.save();
  c.scale(px / 100, px / 100);
  const m = machKit(c, px, env, spec.level, fnToken(spec.type));
  let parts: Part[];
  if (spec.type === 'crate') parts = crateParts(m);
  else if (spec.type === 'gallery') parts = galleryParts(m);
  else {
    const decor = DECOR[spec.type];
    if (!decor) throw new Error(`art: no generator for building "${spec.type}"`);
    parts = [...withTopTrim(m, bodyParts(m)), ...decor(m)];
  }
  drawMachine(m, parts);
  c.restore();
  // the moving parts at rest, on their attach points
  for (const p of MOVING_PARTS) {
    if (p.type !== spec.type || !p.rest) continue;
    c.save();
    c.translate((p.ax - p.px * p.w) * px, (p.ay - p.py * p.h) * px);
    movingPartGen(
      c,
      {
        ...spec,
        gen: 'movingPart',
        group: 'part',
        w: p.w,
        h: p.h,
        pivot: { x: p.px, y: p.py },
        part: p.part,
        index: p.index,
        attach: { x: p.ax, y: p.ay },
      },
      px,
      env,
    );
    c.restore();
  }
};

export const depositGen: Generator<DepositSpec> = (c, spec, px, env) => {
  c.save();
  c.scale(px / 100, px / 100);
  const m = machKit(c, px, env, spec.level, 'machine.crate');
  drawMachine(m, depositParts(m, spec.ore, spec.seed));
  c.restore();
};

export const drillHeadGen: Generator<DrillHeadSpec> = (c, spec, px, env) => {
  c.save();
  c.scale(px / 100, px / 100);
  const m = machKit(c, px, env, spec.level, 'machine.drill');
  drawMachine(m, drillHeadParts(m));
  c.restore();
};

const QUARTER: Readonly<Record<Dir, number>> = { N: 0, E: 1, S: 2, W: 3 };

/**
 * How the canonical bodies (straight S→N, corner S→E) map onto entry `inDir` × exit `out`: quarter turns
 * clockwise, after an optional mirror in x (the corner S→W).
 */
export function chuteTransform(
  inDir: Dir,
  out: Dir,
): { shape: 'straight' | 'corner'; turns: number; mirror: boolean } {
  const i = QUARTER[inDir];
  const o = QUARTER[out];
  if ((i + 2) % 4 === o) return { shape: 'straight', turns: (i + 2) % 4, mirror: false };
  // canonical corner: in S (2), out E (1) → out = in + 3 (turning left, seen from the entry)
  if ((i + 3) % 4 === o) return { shape: 'corner', turns: (i + 2) % 4, mirror: false };
  // mirrored corner: in S (2), out W (3) → out = in + 1
  return { shape: 'corner', turns: (i + 2) % 4, mirror: true };
}

/** Runs `draw` in a 100-unit cell rotated by `turns` quarter turns clockwise (and mirrored in x first). */
function inCell(c: Ctx2D, px: number, turns: number, mirror: boolean, draw: () => void): void {
  c.save();
  c.scale(px / 100, px / 100);
  c.translate(50, 50);
  c.rotate((turns * Math.PI) / 2);
  if (mirror) c.scale(-1, 1);
  c.translate(-50, -50);
  draw();
  c.restore();
}

export const chuteGen: Generator<ChuteSpec> = (c, spec, px, env) => {
  const t = chuteTransform(spec.inDir, spec.out);
  inCell(c, px, t.turns, t.mirror, () => drawChuteBody(c, t.shape, env.pal, t.mirror));
};

/**
 * The chevron overlay: canonical N, turned toward `dir`; with `inDir` the one-cell corner overlay, placed
 * like the corner body (chuteTransform).
 */
export const chevronGen: Generator<ChevronSpec> = (c, spec, px, env) => {
  const ow = outlineUnits(env.sprites.outline, env.sprites.outlineMinPx, px, env.dpr);
  if (spec.inDir !== undefined) {
    const t = chuteTransform(spec.inDir, spec.dir);
    if (t.shape !== 'corner') throw new Error(`art: chevron ${spec.key} is not a corner`);
    // the window: the straight overlay's size in cell units (chevronSize), measured along the arc
    const win = env.sprites.chevronSize * 100;
    inCell(c, px, t.turns, t.mirror, () =>
      drawCornerChevrons(c, win, spec.phase, spec.phases, env.pal, ow),
    );
    return;
  }
  const S = spec.w * 100;
  c.save();
  c.scale(px / 100, px / 100);
  c.translate(S / 2, S / 2);
  c.rotate((QUARTER[spec.dir] * Math.PI) / 2);
  c.translate(-S / 2, -S / 2);
  drawChevrons(c, S, spec.phase, spec.phases, env.pal, ow);
  c.restore();
};

/** Moving parts, drawn in their own frame (w × h cells, cell units from the frame's top-left). */
export const movingPartGen: Generator<MovingPartSpec> = (c, spec, px, env) => {
  const { pal } = env;
  c.save();
  c.scale(px / 100, px / 100);
  const W = spec.w * 100;
  const H = spec.h * 100;
  const m = machKit(c, px, env, 1, fnToken(spec.type));
  const ow = m.ow * 0.8;
  if (spec.part === 'glow') {
    const g = c.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, W / 2);
    g.addColorStop(0, pal.rgba('tier.5', 0.95, 'glow'));
    g.addColorStop(0.45, pal.rgba('tier.5', 0.55, 'glow'));
    g.addColorStop(1, pal.rgba('tier.5', 0, 'glow'));
    c.fillStyle = g;
    c.fillRect(0, 0, W, H);
    c.restore();
    return;
  }
  const opts = { ow, rim: false, halo: null, glow: null, shadeBand: pal.rules.shadeBand, hiW: 2 };
  if (spec.part === 'bit') {
    const steel = pal.sw('material.steel');
    const cone = [
      [W / 2, 3],
      [W - 8, H - 6],
      [8, H - 6],
    ] as const;
    const parts: Part[] = [
      m.poly('material.steel', cone, 3, {
        detail: (cc) => {
          cc.save();
          cc.beginPath();
          poly(cone)(cc);
          cc.clip();
          cc.strokeStyle = steel.shade;
          cc.lineWidth = ow * 0.7;
          for (let i = -1; i < 5; i++) {
            const y = 8 + i * 8 + (spec.index % 2) * 4;
            cc.beginPath();
            cc.moveTo(4, y + 6);
            cc.lineTo(W - 4, y - 2);
            cc.stroke();
          }
          cc.restore();
        },
      }),
    ];
    drawParts(c, parts, opts);
  } else if (spec.part === 'piston') {
    // rod + head inside the frame with their outline (t0 = the outline's reach)
    const t0 = ow + 0.5;
    const hh = H - 2 * t0;
    drawParts(
      c,
      [
        m.rect('material.steel', W * 0.38, t0, W * 0.24, hh * 0.5, 2),
        m.rect('mach.top', t0, t0 + hh * 0.36, W - 2 * t0, hh * 0.64, 4, {
          detail: (cc) =>
            fillP(cc, rrect(t0, t0 + hh * 0.56, W - 2 * t0, 4, 0), pal.hex('machine.press')),
        }),
      ],
      opts,
    );
  } else {
    const gear = regularPts(W / 2, H / 2, W / 2 - 4, 16, -90).map(
      ([x, y], i) =>
        [
          W / 2 + (x - W / 2) * (i % 2 === 0 ? 1 : 0.8),
          H / 2 + (y - H / 2) * (i % 2 === 0 ? 1 : 0.8),
        ] as const,
    );
    drawParts(
      c,
      [
        {
          ...m.ell(fnToken(spec.type), W / 2, H / 2, W / 2 - 4, H / 2 - 4),
          path: smoothPoly(gear),
          detail: (cc) => {
            fillP(cc, circle(W / 2, H / 2, W * 0.16), pal.hex('mach.front'));
            fillP(cc, circle(W / 2, H / 2, W * 0.06), pal.hex('mach.top'));
            for (let i = 0; i < 4; i++) {
              const a = (i / 4) * Math.PI * 2;
              fillP(
                cc,
                circle(W / 2 + Math.cos(a) * W * 0.28, H / 2 + Math.sin(a) * H * 0.28, W * 0.04),
                pal.sw(fnToken(spec.type)).shade,
              );
            }
          },
        },
      ],
      opts,
    );
  }
  c.restore();
};
